import { useEffect, useRef, useState } from 'react';
// All list-page filter state lives under ONE localStorage key, as a nested
// object keyed by page. So inspecting the browser shows a single `filters`
// entry containing { 'purchase-cities': {...}, 'purchase-markets': {...}, ... }.
const ROOT_KEY = 'txerp:filters';
// Legacy per-page prefixes — read-only fallback so older data still hydrates
// on first load after upgrade. Once the user changes any filter, the new write
// goes into ROOT_KEY and the legacy entry is dropped.
const LEGACY_PREFIXES = ['txerp:list-state:', 'pm:list-state:'];
function readRoot() {
    if (typeof window === 'undefined')
        return {};
    try {
        const raw = window.localStorage.getItem(ROOT_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    }
    catch {
        return {};
    }
}
function writeRoot(value) {
    if (typeof window === 'undefined')
        return;
    try {
        window.localStorage.setItem(ROOT_KEY, JSON.stringify(value));
    }
    catch {
        // ignore quota / serialization errors
    }
}
function readLegacy(key) {
    if (typeof window === 'undefined')
        return null;
    for (const prefix of LEGACY_PREFIXES) {
        try {
            const raw = window.localStorage.getItem(prefix + key);
            if (!raw)
                continue;
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object')
                return parsed;
        }
        catch {
            // try next prefix
        }
    }
    return null;
}
function readPersisted(key) {
    const root = readRoot();
    if (root[key])
        return root[key];
    return readLegacy(key);
}
function writePersisted(key, value) {
    if (typeof window === 'undefined')
        return;
    const root = readRoot();
    root[key] = value;
    writeRoot(root);
    // Drop legacy entry for this key once it has been migrated.
    for (const prefix of LEGACY_PREFIXES) {
        try {
            window.localStorage.removeItem(prefix + key);
        }
        catch { /* noop */ }
    }
}
export function clearAllListPageState() {
    if (typeof window === 'undefined')
        return;
    try {
        window.localStorage.removeItem(ROOT_KEY);
        // Also wipe any leftover legacy entries.
        const keysToRemove = [];
        for (let i = 0; i < window.localStorage.length; i++) {
            const k = window.localStorage.key(i);
            if (k && LEGACY_PREFIXES.some((p) => k.startsWith(p)))
                keysToRemove.push(k);
        }
        keysToRemove.forEach((k) => window.localStorage.removeItem(k));
    }
    catch {
        // ignore storage errors
    }
}
export function useListPageState(options) {
    const { storageKey, defaultPageSize = 20, defaultSort = null, defaultFilters = {}, searchDebounceMs = 300, } = options;
    const initial = readPersisted(storageKey);
    const [searchValue, setSearchValue] = useState(initial?.searchValue ?? '');
    const [debouncedSearch, setDebouncedSearch] = useState(initial?.searchValue ?? '');
    const [filterValues, setFilterValues] = useState(initial?.filterValues ?? defaultFilters);
    const [pagination, setPagination] = useState(initial?.pagination ?? { current: 1, pageSize: defaultPageSize });
    const [sortConfig, setSortConfig] = useState(initial?.sortConfig ?? defaultSort);
    const debounceRef = useRef(null);
    useEffect(() => {
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebouncedSearch(searchValue), searchDebounceMs);
        return () => {
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
        };
    }, [searchValue, searchDebounceMs]);
    useEffect(() => {
        writePersisted(storageKey, { searchValue, filterValues, pagination, sortConfig });
    }, [storageKey, searchValue, filterValues, pagination, sortConfig]);
    const resetState = () => {
        setSearchValue('');
        setDebouncedSearch('');
        setFilterValues(defaultFilters);
        setPagination({ current: 1, pageSize: defaultPageSize });
        setSortConfig(defaultSort);
        if (typeof window !== 'undefined') {
            try {
                const root = readRoot();
                if (storageKey in root) {
                    delete root[storageKey];
                    if (Object.keys(root).length === 0) {
                        window.localStorage.removeItem(ROOT_KEY);
                    }
                    else {
                        writeRoot(root);
                    }
                }
                for (const prefix of LEGACY_PREFIXES) {
                    window.localStorage.removeItem(prefix + storageKey);
                }
            }
            catch { /* noop */ }
        }
    };
    return {
        searchValue,
        debouncedSearch,
        setSearchValue,
        filterValues,
        setFilterValues,
        pagination,
        setPagination,
        sortConfig,
        setSortConfig,
        resetState,
    };
}
